import {
  useUserActionPermissionDetails,
  USER_TYPE_TO_PERMISSIONS,
  UserActionPermissionsKey,
  useIsUserActionPermissionVisible,
} from "@src/features/roles/utils/actionPermissions";
import { FC, useCallback, useMemo } from "react";
import { createDefaultActionPermissionSetInputForUserType } from "@src/features/roles/utils/actionPermissions";
import { Input } from "@src/lib/ui/Input";
import useRoleGroups from "@src/features/roles/hooks/useRoleGroups";
import {
  ROLE_USER_TYPE_COPY,
  ROLE_USER_TYPE_DESCRIPTION,
} from "@src/features/app/utils/copy";
import { ActionPermissionSetInput, UserType } from "@src/types.generated";
import { Label } from "@src/lib/ui/Label";
import Text from "@src/lib/ui/Text";
import { RoleGroupThinFragment } from "@src/features/roles/hooks/useRoleGroups.generated";
import { Button } from "@src/lib/ui/Button";
import TrainerIcon from "@src/lib/icons/18px/trainer";
import UserSettingsIcon from "@src/lib/icons/18px/user-settings";
import StudentIcon from "@src/lib/icons/18px/student";
import { Tooltip, TooltipContent, TooltipTrigger } from "@src/lib/ui/Tooltip";
import CircleInfoOutline from "@src/lib/icons/18px/circle-info-outline";
import { Switch } from "@src/lib/ui/Switch";
import Spinner from "@src/lib/ui/Spinner";
import { RoleDialogBody_RoleFragment } from "./RoleDialogBody.generated";
import { Checkbox } from "@src/lib/ui/Checkbox";
import useAvailableRoleLevels from "@src/features/roles/hooks/useAvailableRoleLevels";
import ChevronDown from "@src/lib/icons/18px/chevron-down";
import { RadioGroup } from "@src/lib/ui/RadioGroup";
import RadioGroupChoiceboxItem from "../../../lib/ui/RadioGroupChoiceboxItem";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
} from "@src/lib/ui/DropdownMenu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@src/lib/ui/Select";
import ScrollableFlexGrow from "@src/lib/ui/ScrollableFlexGrow";

const USER_TYPE_TO_SECTION_HEADER = {
  [UserType.Admin]: "Admins",
  [UserType.Manager]: "Managers",
  [UserType.Employee]: "Employees",
};

export type RoleDialogContentProps = {
  name: string;
  setName: (name: string) => void;
  roleUserType: UserType | undefined;
  setRoleUserType: (userType: UserType) => void;
  permissions: ActionPermissionSetInput;
  setPermissions: (permissions: ActionPermissionSetInput) => void;
  clockInConfirmationRequired: boolean;
  setClockInConfirmationRequired: (
    clockInConfirmationRequired: boolean,
  ) => void;
  roleGroupIds: string[];
  setRoleGroupIds: (ids: string[]) => void;
  toggleClockInConfirmationRequired: () => void;
  directReportRoleIds: number[];
  setDirectReportRoleIds: (ids: number[]) => void;
  roleLevelV2: number | undefined;
  setRoleLevelV2: (level: number | undefined) => void;
};

type UserTypeRoleProps = {
  userType: UserType;
  selectedUserType?: UserType | undefined;
  className?: string;
};

export const UserTypeRole: FC<UserTypeRoleProps> = ({
  userType,
  className,
  selectedUserType,
}) => {
  return (
    <RadioGroupChoiceboxItem
      testId={`role-user-type-radio-${userType.toLowerCase()}`}
      value={userType}
      className={className}
      selected={userType === selectedUserType}
    >
      {userType === UserType.Admin && <UserSettingsIcon className="mr-2" />}
      {userType === UserType.Manager && <TrainerIcon className="mr-2" />}
      {userType === UserType.Employee && <StudentIcon className="mr-2" />}
      <Label className="flex flex-col">
        <Text variant="fine-print-title">
          {userType === UserType.Manager
            ? "Manager"
            : ROLE_USER_TYPE_COPY[userType || UserType.Employee]}
        </Text>
        <Text
          variant="fine-print-description"
          className="text-muted-foreground"
        >
          {ROLE_USER_TYPE_DESCRIPTION[userType]}
        </Text>
      </Label>
    </RadioGroupChoiceboxItem>
  );
};

type RoleTrainingOptionsSelectionProps = {
  clockInConfirmationRequired: boolean;
  toggleClockInConfirmationRequired: () => void;
};

export const RoleTrainingOptionsSelection: FC<
  RoleTrainingOptionsSelectionProps
> = ({ clockInConfirmationRequired, toggleClockInConfirmationRequired }) => {
  return (
    <div className="mt-4 flex items-center justify-between self-stretch">
      <Label htmlFor="clock-in-confirmation">
        Require clock-in confirmation to train
      </Label>
      <Switch
        id="clock-in-confirmation"
        checked={clockInConfirmationRequired}
        onCheckedChange={toggleClockInConfirmationRequired}
      />
    </div>
  );
};

type PermissionRowProps = {
  permission: UserActionPermissionsKey;
  permissions: ActionPermissionSetInput;
  setPermissions: (permissions: ActionPermissionSetInput) => void;
  required?: boolean;
};

const PermissionRow: FC<PermissionRowProps> = ({
  permission,
  permissions,
  setPermissions,
  required = false,
}) => {
  const permissionDetails = useUserActionPermissionDetails();
  const checked = useMemo(
    () => required || Boolean(permissions[permission]?.value),
    [permissions, permission, required],
  );
  const toggleValue = useCallback(() => {
    setPermissions({
      ...permissions,
      [permission]: {
        value: !checked,
      },
    });
  }, [setPermissions, permissions, permission, checked]);
  return (
    <div className="flex items-center justify-between self-stretch">
      <div className="flex flex-1 items-center space-x-2 self-stretch">
        <Label
          htmlFor={`permission-${permission}`}
          className="cursor-pointer text-sm font-normal text-foreground select-none"
        >
          {permissionDetails[permission].name}
        </Label>
        {typeof permissionDetails[permission]?.description === "string" && (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <div
                className="cursor-help"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                <CircleInfoOutline className="text-muted-foreground" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              {String(permissionDetails[permission].description)}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      <Checkbox
        id={`permission-${permission}`}
        checked={required || Boolean(permissions[permission]?.value)}
        onCheckedChange={toggleValue}
        disabled={required}
        className="mr-2"
      />
    </div>
  );
};

type RolePermissionOptionsHeaderProps = {
  showManagerLevel: boolean;
  roleLevelV2: number | undefined;
  setRoleLevelV2: (level: number | undefined) => void;
  availableLevels: number[];
};

const RolePermissionOptionsHeader: FC<RolePermissionOptionsHeaderProps> = ({
  showManagerLevel,
  roleLevelV2,
  setRoleLevelV2,
  availableLevels,
}) => {
  if (showManagerLevel && availableLevels.length > 0) {
    const highestLevel = Math.max(...availableLevels);
    return (
      <div className="mt-3 mb-2 flex items-center justify-between self-stretch">
        <Label htmlFor="manager-level">Manager Level</Label>
        <Select
          value={roleLevelV2?.toString() || ""}
          onValueChange={(value) =>
            setRoleLevelV2(value ? parseInt(value) : undefined)
          }
        >
          <SelectTrigger id="manager-level" className="w-[75px]">
            <SelectValue placeholder={highestLevel} />
          </SelectTrigger>
          <SelectContent>
            {availableLevels.map((level) => (
              <SelectItem
                key={level}
                value={level.toString()}
                className="cursor-pointer"
              >
                {level}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  return (
    <Text variant="fine-print-title" className="mt-3">
      Permission options
    </Text>
  );
};

type RolePermissionOptionsSelectionProps = {
  selectedUserType: UserType;
  permissions: ActionPermissionSetInput;
  setPermissions: (permissions: ActionPermissionSetInput) => void;
};

const RolePermissionOptionsSelection: FC<
  RolePermissionOptionsSelectionProps
> = (props) => {
  const { selectedUserType } = props;
  const isVisible = useIsUserActionPermissionVisible();
  const { required, optionalDefaultChecked, optionalDefaultUnchecked } =
    USER_TYPE_TO_PERMISSIONS[selectedUserType];
  const visibleRequired = required.filter(isVisible);
  const visibleOptionalChecked = optionalDefaultChecked.filter(isVisible);
  const visibleOptionalUnchecked = (optionalDefaultUnchecked || []).filter(
    isVisible,
  );
  if (
    visibleRequired.length +
      visibleOptionalChecked.length +
      visibleOptionalUnchecked.length ===
    0
  ) {
    return null;
  }
  return (
    <div className="flex flex-col space-y-2 overflow-auto">
      {visibleRequired.map((permission) => (
        <PermissionRow
          {...props}
          permission={permission}
          key={permission}
          required
        />
      ))}
      {visibleOptionalChecked.map((permission) => (
        <PermissionRow {...props} permission={permission} key={permission} />
      ))}
      {visibleOptionalUnchecked.map((permission) => (
        <PermissionRow {...props} permission={permission} key={permission} />
      ))}
    </div>
  );
};

type RoleGroupMultiSelectFieldProps = {
  roleGroupIds: string[];
  setRoleGroupIds: (x: string[]) => void;
  roleGroupOptions?: RoleGroupThinFragment[];
  dataTestId?: string;
};

export const RoleGroupMultiSelectField: FC<RoleGroupMultiSelectFieldProps> = (
  props,
) => {
  const { roleGroups } = useRoleGroups();
  return (
    <div className="flex flex-col space-y-1 self-stretch">
      <Text variant="compact-title" className="mb-2" multiline>
        Departments
      </Text>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="mb-1 flex w-full shrink-0 justify-between truncate"
            data-testid={props.dataTestId}
          >
            <div className="truncate">
              {props.roleGroupIds.length === 0
                ? "Select Departments"
                : roleGroups
                    .filter((group) => props.roleGroupIds.includes(group.id))
                    .map((group) => group.name)
                    .join(", ")}
            </div>
            <ChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="h-[240px] dropdown-menu-content-w-full overflow-y-auto">
          {roleGroups.map((group) => (
            <DropdownMenuCheckboxItem
              key={group.id}
              onSelect={(e) => e.preventDefault()}
              checked={props.roleGroupIds.includes(group.id)}
              onCheckedChange={(checked) => {
                if (checked) {
                  props.setRoleGroupIds([...props.roleGroupIds, group.id]);
                } else {
                  props.setRoleGroupIds(
                    props.roleGroupIds.filter((id) => id !== group.id),
                  );
                }
              }}
            >
              <Text variant="compact-description">{group.name}</Text>
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

type DirectReportsProps = {
  directReportRoleIds: number[];
  setDirectReportRoleIds: (directReportRoleIds: number[]) => void;
  roles: RoleDialogBody_RoleFragment[];
};

export const DirectReports: FC<DirectReportsProps> = ({
  directReportRoleIds,
  setDirectReportRoleIds,
  roles,
}) => {
  return (
    <>
      <Text variant="compact-title" className="mb-1" multiline>
        Direct Reports
      </Text>
      <Text
        variant="fine-print-description"
        className="mb-2 text-muted-foreground"
        multiline
      >
        Select roles that directly report to this role. Users are responsible
        for the check-ins and tasks of their direct reports.
      </Text>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="mb-1 flex w-full shrink-0 justify-between truncate"
          >
            <div className="truncate">
              {directReportRoleIds.length === 0
                ? "Select Roles"
                : roles
                    .filter((role) => directReportRoleIds.includes(role.id))
                    .map((role) => role.name)
                    .join(", ")}
            </div>
            <ChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="h-[240px] dropdown-menu-content-w-full overflow-y-auto">
          {Object.values(UserType).map((userType, index) => {
            const filteredRoles = roles.filter((role) => {
              const type = role.roleActionPermissions?.roleUserType;
              if (!type && userType === UserType.Employee) return true;
              return type === userType;
            });
            if (filteredRoles.length === 0) return null;

            return (
              <DropdownMenuGroup key={userType}>
                <DropdownMenuLabel className="flex items-center justify-between">
                  <Text variant="fine-print-title" className="text-gray-8">
                    {USER_TYPE_TO_SECTION_HEADER[userType]}
                  </Text>
                  <Text
                    variant="fine-print-title"
                    className="cursor-pointer text-primary hover:underline"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const roleIds = filteredRoles.map((role) => role.id);
                      setDirectReportRoleIds(
                        directReportRoleIds.includes(roleIds[0])
                          ? directReportRoleIds.filter(
                              (id) => !roleIds.includes(id),
                            )
                          : [...directReportRoleIds, ...roleIds],
                      );
                    }}
                  >
                    {directReportRoleIds.includes(filteredRoles[0]?.id)
                      ? "Deselect All"
                      : "Select All"}
                  </Text>
                </DropdownMenuLabel>
                {filteredRoles.map((role) => (
                  <DropdownMenuCheckboxItem
                    key={role.id}
                    onSelect={(e) => e.preventDefault()}
                    checked={directReportRoleIds.includes(role.id)}
                    onCheckedChange={() => {
                      setDirectReportRoleIds(
                        directReportRoleIds.includes(role.id)
                          ? directReportRoleIds.filter((id) => id !== role.id)
                          : [...directReportRoleIds, role.id],
                      );
                    }}
                  >
                    <div className="flex w-full justify-between">
                      <Text variant="compact-description">{role.name}</Text>
                      <Text
                        variant="fine-print-description"
                        className="font-mono"
                      >
                        {role.userCount}
                      </Text>
                    </div>
                  </DropdownMenuCheckboxItem>
                ))}
                {index < Object.values(UserType).length - 1 && (
                  <DropdownMenuSeparator />
                )}
              </DropdownMenuGroup>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};

const RoleDialogContent: FC<RoleDialogContentProps> = ({
  name,
  setName,
  roleUserType,
  setRoleUserType,
  permissions,
  setPermissions,
  clockInConfirmationRequired,
  setClockInConfirmationRequired,
  roleGroupIds,
  setRoleGroupIds,
  toggleClockInConfirmationRequired,
  directReportRoleIds,
  setDirectReportRoleIds,
  roleLevelV2,
  setRoleLevelV2,
}) => {
  const {
    roles,
    loading: rolesLoading,
    maxRoleLevel,
    availableRoleLevels,
  } = useAvailableRoleLevels();

  const setSelectedUserTypeAndDefaultPermissionsForRole = useCallback(
    (v: string) => {
      const userType = v as UserType;
      setRoleUserType(userType);
      setClockInConfirmationRequired(userType !== UserType.Admin);
      setPermissions(
        createDefaultActionPermissionSetInputForUserType(userType),
      );
      if (userType === UserType.Manager && maxRoleLevel > 0) {
        setRoleLevelV2(maxRoleLevel);
      } else {
        setRoleLevelV2(undefined);
      }
    },
    [
      setClockInConfirmationRequired,
      setPermissions,
      setRoleUserType,
      setRoleLevelV2,
      maxRoleLevel,
    ],
  );

  if (rolesLoading) {
    return (
      <div className="flex flex-1 items-center justify-center py-8">
        <Spinner size={60} />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-row" data-testid="role-dialog">
      <ScrollableFlexGrow
        flex="none"
        className="w-[420px] shrink-0 border-r border-border"
        innerClassName="gap-4 p-6"
      >
        <div className="space-y-2">
          <Label htmlFor="role-name">Role Name</Label>
          <Input
            id="role-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="role-name-input"
            placeholder="e.g., Dishwasher or GM"
          />
        </div>
        <RoleGroupMultiSelectField
          roleGroupIds={roleGroupIds}
          setRoleGroupIds={setRoleGroupIds}
        />
        <DirectReports
          directReportRoleIds={directReportRoleIds}
          setDirectReportRoleIds={setDirectReportRoleIds}
          roles={roles}
        />
        {roleUserType && (
          <RoleTrainingOptionsSelection
            clockInConfirmationRequired={clockInConfirmationRequired}
            toggleClockInConfirmationRequired={
              toggleClockInConfirmationRequired
            }
          />
        )}
      </ScrollableFlexGrow>
      <ScrollableFlexGrow
        className="flex-1 bg-gray-1"
        innerClassName="gap-4 p-6"
      >
        <Label>Permissions</Label>
        <div className="flex flex-col rounded-md border border-input bg-background p-4 text-sm shadow-sm">
          <div className="flex flex-col self-stretch">
            <RadioGroup
              value={roleUserType}
              onValueChange={setSelectedUserTypeAndDefaultPermissionsForRole}
              defaultValue={roleUserType}
            >
              <UserTypeRole
                userType={UserType.Admin}
                selectedUserType={roleUserType}
                className="mb-2"
              />
              <UserTypeRole
                userType={UserType.Manager}
                selectedUserType={roleUserType}
                className="mb-2"
              />
              <UserTypeRole
                userType={UserType.Employee}
                selectedUserType={roleUserType}
              />
            </RadioGroup>
          </div>
          {roleUserType && (
            <>
              <RolePermissionOptionsHeader
                showManagerLevel={roleUserType === UserType.Manager}
                roleLevelV2={roleLevelV2}
                setRoleLevelV2={setRoleLevelV2}
                availableLevels={availableRoleLevels}
              />
              <RolePermissionOptionsSelection
                selectedUserType={roleUserType}
                permissions={permissions}
                setPermissions={setPermissions}
              />
            </>
          )}
        </div>
      </ScrollableFlexGrow>
    </div>
  );
};

export default RoleDialogContent;
